import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

import type { Student } from '~entities/student/modal/student';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import { type ParentDetails } from '../model/parent.type';

export type ParentWithStudents = ParentDetails & {
  students: Array<Student>;
};

interface GetParentByStudioParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tags?: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ParentResponse {
  data: Array<ParentWithStudents>;
  pagination: PaginationMeta;
  tags: Array<{ _id: string; fieldName: string }>;
}

export const getParentByStudio = async ({
  page = 1,
  limit = 10,
  search = '',
  sortBy,
  sortOrder,
  tags,
}: GetParentByStudioParams = {}) => {
  const response = await baseAPI.get<ParentResponse>('/parents/studio', {
    params: {
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      tags,
    },
  });
  return response.data;
};

export const getParentByStudioOptions = (
  params: GetParentByStudioParams = {},
) => {
  const queryKey = [
    KEYS.PARENTS_LIST,
    KEYS.STUDIO,
    params.page,
    params.limit,
    params.search,
    params.sortBy,
    params.sortOrder,
    params.tags,
  ];

  return queryOptions({
    queryKey,
    queryFn: () => getParentByStudio(params),
    staleTime: 0, // Always fetch fresh data when query key changes
    placeholderData: keepPreviousData,
  });
};

export const useGetParentByStudio = (params: GetParentByStudioParams = {}) => {
  return useQuery(getParentByStudioOptions(params));
};
